import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/contexts/auth-context";

interface MyAccount {
  id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
  onboarding_completed_at: string | null; // ADV-1280
  is_demo: boolean; // ADV-1275
  demo_created_by: string | null; // ADV-1275
  demo_loaded_at: string | null; // ADV-1275
}

export interface MyAccountsResponse {
  accounts: MyAccount[];
  active_account_id: string;
}

interface SwitchAccountResponse {
  success: boolean;
  account_id: string;
}

export function useMyAccounts() {
  return useQuery({
    queryKey: ["my-accounts"],
    queryFn: () => api.get<MyAccountsResponse>("/core/me/accounts"),
  });
}

export function useSwitchAccount() {
  const queryClient = useQueryClient();
  const { refreshSession } = useAuth();

  return useMutation({
    mutationFn: (accountId: string) =>
      api.post<SwitchAccountResponse>("/core/me/switch-account", {
        account_id: accountId,
      }),
    onSuccess: async () => {
      await refreshSession();
      queryClient.invalidateQueries();
    },
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  const { refreshSession } = useAuth();

  return useMutation({
    mutationFn: () =>
      api.post<{ success: boolean }>("/core/me/onboarding/complete", {}),
    onSuccess: async () => {
      // Optimistically stamp the active account in cache. On /welcome the
      // WelcomeGuard is unmounted, so there is no active ["my-accounts"]
      // observer — invalidateQueries (refetchType: "active") is therefore a
      // no-op there and leaves the cached onboarding_completed_at at its stale
      // null. Without this write, navigate("/dashboard") remounts the guard,
      // which reads that stale null synchronously and bounces the user right
      // back to /welcome before the background refetch lands (ADV-1280).
      queryClient.setQueryData<MyAccountsResponse>(["my-accounts"], (prev) =>
        prev
          ? {
              ...prev,
              accounts: prev.accounts.map((a) =>
                a.id === prev.active_account_id
                  ? { ...a, onboarding_completed_at: new Date().toISOString() }
                  : a,
              ),
            }
          : prev,
      );
      // The server write already succeeded here. A refreshSession() hiccup must
      // not reject the mutation (which would present a successful onboarding as
      // a total failure to the caller) nor block the cache invalidation below.
      try {
        await refreshSession();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[useCompleteOnboarding] refreshSession failed:", error);
      }
      // Reconcile against server truth (server stamps now() authoritatively).
      await queryClient.invalidateQueries({ queryKey: ["my-accounts"] });
    },
    // Surface genuine write failures instead of swallowing them (ADV-1280).
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error("[useCompleteOnboarding]", error);
    },
  });
}
